import { FilterMatchMode } from "primereact/api";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { useState } from "react";
import { OpcUaUserDTO } from "../../serviceapi/dataCollectorApi";
import PasswordUserDialog from "./PasswordUserDialog";
import { formatCreatedAt } from "./opcUaUtils";

interface PasswordUsersTabProps {
  users: OpcUaUserDTO[];
  isLoading: boolean;
  fetchUsers: () => Promise<void>;
  saveUser: (username: string, password: string, enabled: boolean) => Promise<boolean>;
  changePassword: (
    username: string,
    password: string,
    enabled: boolean
  ) => Promise<boolean>;
  setUserEnabled: (username: string, enabled: boolean) => Promise<void>;
  deleteUser: (username: string) => Promise<void>;
}

const PasswordUsersTab = ({
  users,
  isLoading,
  fetchUsers,
  saveUser,
  changePassword,
  setUserEnabled,
  deleteUser,
}: PasswordUsersTabProps) => {
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");
  /** undefined = closed, null = create, a user = change that account's password. */
  const [dialogUser, setDialogUser] = useState<OpcUaUserDTO | null | undefined>(
    undefined
  );

  const onGlobalFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setGlobalFilterValue(value);
    setFilters((prevFilters) => ({
      ...prevFilters,
      global: { ...prevFilters.global, value },
    }));
  };

  const handleDelete = (username: string) => {
    confirmDialog({
      message: `Delete OPC UA user "${username}"? Any client authenticating with these credentials will stop connecting.`,
      header: "Delete user",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger",
      accept: () => deleteUser(username),
    });
  };

  const enabledBodyTemplate = (user: OpcUaUserDTO) => (
    <InputSwitch
      checked={!!user.enabled}
      onChange={(e) => setUserEnabled(user.username as string, e.value as boolean)}
      className="toggle-button-custom"
      tooltip={user.enabled ? "Disable account" : "Enable account"}
      disabled={!user.username}
    />
  );

  const actionBodyTemplate = (user: OpcUaUserDTO) => (
    <div className="flex justify-content-end gap-1">
      <Button
        className="p-button-text p-button-sm"
        icon="pi pi-key"
        tooltip="Change password"
        onClick={() => setDialogUser(user)}
        disabled={!user.username}
      />
      <Button
        className="p-button-text p-button-danger p-button-sm"
        icon="pi pi-trash"
        tooltip="Delete"
        onClick={() => handleDelete(user.username as string)}
        disabled={!user.username}
      />
    </div>
  );

  return (
    <div className="flex flex-column gap-3" style={{ height: "100%" }}>
      <div className="flex justify-content-between align-items-center gap-2 flex-wrap">
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText
            type="search"
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Search users"
          />
        </IconField>
        <div className="flex gap-2">
          <Button
            label="Add user"
            icon="pi pi-plus"
            className="p-button-sm"
            onClick={() => setDialogUser(null)}
          />
          <Button
            className="p-button-text p-button-sm"
            icon="pi pi-refresh"
            tooltip="Refresh"
            onClick={fetchUsers}
            disabled={isLoading}
          />
        </div>
      </div>

      {/* loading is a prop, not a spinner swapped in for the table — see ApplicationCertificatesTab. */}
      <DataTable
        value={users}
        loading={isLoading}
        dataKey="username"
        size="small"
        scrollable
        scrollHeight="flex"
        emptyMessage="No username/password accounts configured."
        filters={filters}
        onFilter={(e) => setFilters(e.filters)}
        globalFilterFields={["username"]}
        style={{ flex: 1, minHeight: 0 }}
      >
        <Column field="username" header="Username" sortable />
        <Column
          header="Enabled"
          body={enabledBodyTemplate}
          style={{ width: "7rem" }}
        />
        <Column
          field="createdAt"
          header="Created"
          sortable
          body={(user: OpcUaUserDTO) => formatCreatedAt(user.createdAt)}
          style={{ width: "12rem" }}
        />
        <Column body={actionBodyTemplate} style={{ width: "7rem" }} />
      </DataTable>

      {dialogUser !== undefined && (
        <PasswordUserDialog
          username={dialogUser?.username}
          currentEnabled={dialogUser?.enabled}
          existingUsernames={users
            .map((user) => user.username)
            .filter((username): username is string => !!username)}
          onCreate={saveUser}
          onChangePassword={changePassword}
          close={() => setDialogUser(undefined)}
        />
      )}
    </div>
  );
};

export default PasswordUsersTab;
